import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Chip, Modal, SkeletonRows } from '../components/ui';
import { useToast } from '../components/Toasts';
import { api, ApiRequestError } from '../lib/api';

/**
 * Who can sign in to a school's own dashboard.
 *
 * Creating a school invited one person and that was the end of it — no way to
 * add a second, replace one who left, or even see whether the first ever
 * accepted. A school with one administrator who leaves is a school locked out
 * of its own records, and the only sign of it is a support call.
 */

interface Admin {
  membershipId: string;
  userId: string;
  name: string;
  email: string | null;
  phone: string;
  role: 'PRINCIPAL' | 'SCHOOL_ADMIN';
  status: 'INVITED' | 'ACTIVE' | 'SUSPENDED';
  hasSignedIn: boolean;
}

interface Credentials {
  username: string;
  tempPassword: string;
  expiresAt: string;
}

interface Invite {
  id: string;
  name: string;
  email: string;
  role: string;
  expiresAt: string;
  expired: boolean;
}

const ROLE = { PRINCIPAL: 'Principal', SCHOOL_ADMIN: 'School admin' } as const;

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export function SchoolAdmins({ schoolId }: { schoolId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const [issued, setIssued] = useState<{
    name: string;
    email: string;
    credentials: Credentials | null;
  } | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['school-admins', schoolId],
    queryFn: () => api.get<{ admins: Admin[]; invites: Invite[] }>(`/api/schools/${schoolId}/admins`),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['school-admins', schoolId] });
  const failed = (error: unknown) =>
    toast.push(error instanceof ApiRequestError ? error.message : 'Something went wrong', 'error');

  const setStatus = useMutation({
    mutationFn: (v: { membershipId: string; status: 'ACTIVE' | 'SUSPENDED' }) =>
      api.patch(`/api/schools/${schoolId}/admins/${v.membershipId}`, { status: v.status }),
    onSuccess: (_r, v) => {
      toast.push(v.status === 'ACTIVE' ? 'Access restored' : 'Access removed', 'success');
      refresh();
    },
    onError: failed,
  });

  const revoke = useMutation({
    mutationFn: (inviteId: string) => api.delete(`/api/schools/${schoolId}/invites/${inviteId}`),
    onSuccess: () => {
      toast.push('Invitation cancelled', 'success');
      refresh();
    },
    onError: failed,
  });

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div className="card__title">Administrators</div>
        <span className="caption">Who can sign in to this school's dashboard</span>
        <button className="btn btn--soft btn--sm" style={{ marginLeft: 'auto' }} onClick={() => setInviting(true)}>
          Add an admin
        </button>
      </div>

      {isPending || !data ? (
        <SkeletonRows count={2} />
      ) : (
        <>
          {data.admins.length === 0 ? (
            <div className="caption">Nobody has accepted an invitation yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {data.admins.map((admin) => (
                <div
                  key={admin.membershipId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '11px 0',
                    borderBottom: '1px solid var(--rule)',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{admin.name}</span>
                    <span className="caption mono">{admin.email ?? admin.phone}</span>
                  </div>
                  <Chip tone="neutral">{ROLE[admin.role]}</Chip>
                  {admin.status === 'SUSPENDED' ? (
                    <Chip tone="danger">No access</Chip>
                  ) : admin.hasSignedIn ? (
                    <Chip tone="success">Active</Chip>
                  ) : (
                    // The question a school asks when nobody can get in.
                    <Chip tone="warn">Never signed in</Chip>
                  )}
                  <button
                    className={admin.status === 'SUSPENDED' ? 'btn btn--soft btn--sm' : 'btn btn--danger btn--sm'}
                    style={{ marginLeft: 'auto' }}
                    disabled={setStatus.isPending}
                    onClick={() =>
                      setStatus.mutate({
                        membershipId: admin.membershipId,
                        status: admin.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED',
                      })
                    }
                  >
                    {admin.status === 'SUSPENDED' ? 'Restore access' : 'Remove access'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {data.invites.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="label">Invited, not yet accepted</div>
              {data.invites.map((invite) => (
                <div
                  key={invite.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 14px',
                    borderRadius: 'var(--r-input)',
                    background: 'var(--marigold-tint)',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontSize: 13.5, fontWeight: 600 }}>{invite.name}</span>
                  <span className="caption mono">{invite.email}</span>
                  {invite.expired ? (
                    <Chip tone="danger">Expired {shortDate(invite.expiresAt)}</Chip>
                  ) : (
                    <Chip tone="warn">Expires {shortDate(invite.expiresAt)}</Chip>
                  )}
                  <button
                    className="btn btn--danger btn--sm"
                    style={{ marginLeft: 'auto' }}
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(invite.id)}
                  >
                    Cancel
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </>
      )}

      {inviting ? (
        <InviteModal
          schoolId={schoolId}
          onClose={() => setInviting(false)}
          onInvited={(result) => {
            setInviting(false);
            setIssued(result);
            refresh();
          }}
          onError={failed}
        />
      ) : null}

      {issued ? <IssuedModal issued={issued} onClose={() => setIssued(null)} /> : null}
    </div>
  );
}

function InviteModal({
  schoolId,
  onClose,
  onInvited,
  onError,
}: {
  schoolId: string;
  onClose: () => void;
  onInvited: (result: { name: string; email: string; credentials: Credentials | null }) => void;
  onError: (error: unknown) => void;
}) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '+91',
    role: 'SCHOOL_ADMIN' as 'SCHOOL_ADMIN' | 'PRINCIPAL',
  });

  const invite = useMutation({
    mutationFn: () =>
      api.post<{ name: string; email: string; credentials: Credentials | null }>(
        `/api/schools/${schoolId}/admins`,
        {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          role: form.role,
        },
      ),
    onSuccess: (result) => onInvited(result),
    onError,
  });

  const ready =
    form.name.trim().length >= 2 &&
    /\S+@\S+\.\S+/.test(form.email) &&
    /^\+[1-9]\d{7,14}$/.test(form.phone.trim());

  return (
    <Modal
      title="Add an administrator"
      onClose={onClose}
      footer={
        <>
          <button className="btn" disabled={!ready || invite.isPending} onClick={() => invite.mutate()}>
            {invite.isPending ? 'Creating…' : 'Create the account'}
          </button>
          <button className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
        </>
      }
    >
      <label className="field">
        <span>Name</span>
        <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Meera Kulkarni" />
      </label>

      <label className="field">
        <span>Email</span>
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          placeholder="meera@greenfield.edu.in"
        />
        <span className="field__hint">They sign in with this address.</span>
      </label>

      <label className="field">
        <span>Mobile number</span>
        <input
          value={form.phone}
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          placeholder="+919856412300"
        />
        <span className="field__hint">
          Every account is keyed on a mobile number, and it is how they would use
          the app if they ever needed to.
        </span>
      </label>

      <div className="field">
        <span>Role</span>
        <div className="segmented">
          {(['SCHOOL_ADMIN', 'PRINCIPAL'] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={form.role === value}
              onClick={() => setForm((f) => ({ ...f, role: value }))}
            >
              {ROLE[value]}
            </button>
          ))}
        </div>
        <span className="field__hint">Both see the whole school. The label is what staff see.</span>
      </div>
    </Modal>
  );
}

function IssuedModal({
  issued,
  onClose,
}: {
  issued: { name: string; email: string; credentials: Credentials | null };
  onClose: () => void;
}) {
  const toast = useToast();

  if (!issued.credentials) {
    // They already had an account — at another school, or as staff here. Making
    // up a password for them would lock them out of the other one.
    return (
      <Modal
        title={`${issued.name} added`}
        onClose={onClose}
        footer={
          <button className="btn" onClick={onClose}>
            Done
          </button>
        }
      >
        <div className="banner banner--success">
          {issued.name} already had an ESkoolHub account, so they keep the password
          they already use. Nothing to hand over — they can sign in now.
        </div>
      </Modal>
    );
  }

  const { username, tempPassword } = issued.credentials;
  const block = `ESkoolHub — ${issued.name}\nhttps://school.eskoolhub.com\nUsername: ${username}\nTemporary password: ${tempPassword}`;

  return (
    <Modal
      title="Hand these over"
      onClose={onClose}
      footer={
        <>
          <button
            className="btn"
            onClick={() => {
              void navigator.clipboard.writeText(block);
              toast.push('Copied', 'success');
            }}
          >
            Copy
          </button>
          <button className="btn btn--ghost" onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      <div className="banner banner--success">
        Shown once. Only a fingerprint of the password is stored, so it cannot be
        looked up again — copy it before closing. If it is lost, remove the
        administrator and add them again.
      </div>

      <div className="fields-plain" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label className="field">
          <span>Username</span>
          <input readOnly value={username} style={{ fontFamily: 'var(--font-mono)' }} />
        </label>
        <label className="field">
          <span>Temporary password</span>
          <input readOnly value={tempPassword} style={{ fontFamily: 'var(--font-mono)', fontSize: 16 }} />
          <span className="field__hint">
            They must set their own password before they can do anything else — the
            API refuses every other request until they have.
          </span>
        </label>
      </div>
    </Modal>
  );
}
